// @flow

import * as React from "react"
import {withTranslation} from "react-i18next"
import {set, debounce, cloneDeep} from "lodash"
import Flatpickr from "react-flatpickr"
import ClassNames from "classnames"

import InvoicePersonEditModal from "../modals/invoice-person-edit"
import type {Dispatch, InvoiceBase, InvoicePerson} from "../../types"

type Props = {
  +dispatch: Dispatch,
  +t: any,
  +invoice: InvoiceBase,
  +onUpdate: (InvoiceBase) => void,
  +finaliseError: boolean,
}

type State = {
  +edits: {
    title: string,
    invoiceDate: string,
    dueDate: string,
    invoiceNbr: string,
  },
  +isSenderEditModalOpen: boolean,
  +isReceiverEditModalOpen: boolean,
}

type FlatpickrInstance = any

class InvoiceHeader extends React.Component<Props, State> {
  handleDateChange: ($ReadOnlyArray<Date>, string, FlatpickrInstance) => void
  handleInputChange: (SyntheticEvent<HTMLInputElement>) => void
  handleEditReceiverClick: (SyntheticEvent<HTMLLinkElement>) => void
  handleEditSenderClick: (SyntheticEvent<HTMLLinkElement>) => void
  handleSavePerson: (type: "sender" | "receiver", edits: InvoicePerson) => void

  handleCloseModal: (SyntheticEvent<HTMLLinkElement>) => void
  persistUpdate: (any) => void
  invoiceDateRef: any
  invoiceDueDateRef: any

  constructor(props: Props) {
    super(props)

    this.state = {
      edits: {
        id: props.invoice.id,
        title: props.invoice.title,
        dueDate: props.invoice.dueDate ? props.invoice.dueDate : "",
        invoiceDate: props.invoice.invoiceDate ? props.invoice.invoiceDate : "",
        invoiceNbr: props.invoice.invoiceNbr,
      },
      isSenderEditModalOpen: false,
      isReceiverEditModalOpen: false,
    }

    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleDateChange = this.handleDateChange.bind(this)
    this.persistUpdate = debounce(this.persistUpdate.bind(this), 200, {
      leading: false,
      trailing: true,
    })
    this.handleEditReceiverClick = this.handleEditReceiverClick.bind(this)
    this.handleEditSenderClick = this.handleEditSenderClick.bind(this)
    this.handleCloseModal = this.handleCloseModal.bind(this)
    this.handleSavePerson = this.handleSavePerson.bind(this)
  }

  static getDerivedStateFromProps({invoice}: Props, state: State) {
    if (invoice && invoice.dueDate != state.edits.dueDate) {
      return {
        edits: {
          id: invoice.id,
          title: invoice.title,
          dueDate: invoice.dueDate,
          invoiceDate: invoice.invoiceDate,
          invoiceNbr: invoice.invoiceNbr,
        },
      }
    }
    return null
  }

  componentDidUpdate(prevProps: Props) {
    const {finaliseError} = this.props
    if (finaliseError !== prevProps.finaliseError) {
      let {altInput: altInputDate} = this.invoiceDateRef.flatpickr
      let {altInput: altInputDueDate} = this.invoiceDueDateRef.flatpickr
      if (finaliseError) {
        altInputDate.classList.add("is-invalid")
        altInputDueDate.classList.add("is-invalid")
      } else {
        altInputDate.classList.remove("is-invalid")
        altInputDueDate.classList.remove("is-invalid")
      }
    }
  }

  handleInputChange(e) {
    const {name, value} = e.target
    this.editBabyListInvoice(name, value)
  }

  handleDateChange(
    dates: $ReadOnlyArray<Date>,
    dateStr: string,
    instance: FlatpickrInstance
  ) {
    dates.forEach((d) => d)
    this.editBabyListInvoice(instance.element.name, dateStr)
  }

  handleEditReceiverClick(e: SyntheticEvent<HTMLLinkElement>) {
    e.preventDefault()
    this.setState({
      isSenderEditModalOpen: false,
      isReceiverEditModalOpen: true,
    })
  }

  handleEditSenderClick(e: SyntheticEvent<HTMLLinkElement>) {
    e.preventDefault()
    this.setState({
      isSenderEditModalOpen: true,
      isReceiverEditModalOpen: false,
    })
  }

  handleCloseModal(e: SyntheticEvent<HTMLLinkElement>) {
    e.preventDefault()
    this.setState({
      isSenderEditModalOpen: false,
      isReceiverEditModalOpen: false,
    })
  }

  editBabyListInvoice(property: string, value: any) {
    // TODO
    // yes this is not really a functional style I know
    // but at least it mutates a copy
    const edits = cloneDeep(this.state.edits)
    set(edits, property, value)
    this.setState({edits}, () => {
      this.persistUpdate(edits)
    })
  }

  persistUpdate(edits: any) {
    this.props.onUpdate(edits)
  }

  renderCityLine(postalCode, city, country) {
    let line = postalCode ? postalCode : ""
    line = line.length ? `${line} ${city.trim()}` : `${city.trim()}`
    line = line.length ? `${line}, ` : ""
    line = country && country.length ? `${line}${country.trim()}` : line
    return line.length ? line : "-"
  }

  renderSender() {
    const {t, invoice} = this.props
    const {
      firstname,
      lastname,
      street,
      nbr,
      box,
      email,
      postalCode,
      city,
      country,
      vat,
      company,
      accountNbr,
    } = invoice.sender
    const cityLine = this.renderCityLine(postalCode, city, country)
    return (
      <div className="sender">
        <label htmlFor="sender">{t("invoice.sender")}</label>
        <div>
          {firstname} {lastname}
          <br />
          {company}
          <div className="mt-2">
            {street} {nbr}
            {box && <React.Fragment> ({box})</React.Fragment>}
            <br />
            {cityLine}
            <br />
            {email}
          </div>
          <div className="mt-2">
            <span>{t("invoice.vat")}: </span>
            {vat}
            <br />
            <span>{t("invoice.accountNbr")}: </span>
            {accountNbr}
          </div>
          {invoice.isEditable && (
            <a
              href="#"
              className="d-block mt-2"
              onClick={this.handleEditSenderClick}
            >
              {t("invoice.edit")}
            </a>
          )}
        </div>
      </div>
    )
  }

  renderReceiver() {
    const {t, invoice} = this.props
    const {
      firstname,
      lastname,
      partner1Firstname,
      partner1Lastname,
      partner2Firstname,
      partner2Lastname,
      street,
      nbr,
      box,
      email,
      postalCode,
      city,
      country,
      company,
      vat,
    } = invoice.receiver
    const cityLine = this.renderCityLine(postalCode, city, country)
    return (
      <div className="receiver">
        <label htmlFor="receiver">{t("invoice.receiver")}</label>
        <div>
          {vat ? (
            <>
              {firstname} {lastname} {company ? ` / ${company}` : ""}
            </>
          ) : (
            <>
              {partner1Firstname} {partner1Lastname}
              {" & "}
              {partner2Firstname} {partner2Lastname}
            </>
          )}
          <div className="mt-2">
            {street} {nbr}
            {box && <React.Fragment> ({box})</React.Fragment>}
            <br />
            {cityLine}
            <br />
            {email}
            {invoice.receiver.emailPartner && (
              <>, {invoice.receiver.emailPartner}</>
            )}
          </div>
          {invoice.isEditable && (
            <a
              href="#"
              className="d-block mt-2"
              onClick={this.handleEditReceiverClick}
            >
              {t("invoice.edit")}
            </a>
          )}
        </div>
      </div>
    )
  }

  handleSavePerson(type: "sender" | "receiver", edits: InvoicePerson) {
    const {invoice, onUpdate} = this.props
    const editedInvoice =
      type == "sender"
        ? {...invoice, sender: edits}
        : {...invoice, receiver: edits}
    onUpdate(editedInvoice)
  }

  renderModal() {
    const {isSenderEditModalOpen, isReceiverEditModalOpen} = this.state
    const {t, invoice} = this.props
    if (isSenderEditModalOpen) {
      return (
        <InvoicePersonEditModal
          person={invoice.sender}
          title={t(`invoice.senderEditTitle`)}
          onSave={(edits: InvoicePerson) =>
            this.handleSavePerson("sender", edits)
          }
          onCloseModal={this.handleCloseModal}
        />
      )
    }
    if (isReceiverEditModalOpen) {
      return (
        <InvoicePersonEditModal
          person={invoice.receiver}
          title={t(`invoice.receiverEditTitle`)}
          onSave={(edits: InvoicePerson) =>
            this.handleSavePerson("receiver", edits)
          }
          onCloseModal={this.handleCloseModal}
        />
      )
    }
  }

  render() {
    const {t, invoice, finaliseError} = this.props
    const {edits} = this.state
    const classNameForm = ClassNames("form-control", {
      "is-invalid": finaliseError,
    })
    return (
      <React.Fragment>
        <header className="invoice-sheet-header">
          <div className="row">
            <div className="col-md-4">{this.renderSender()}</div>
            <div className="col-md-4">{this.renderReceiver()}</div>
            <div className="col-md-3 offset-md-1">
              <div className="invoice-sheet-header-date-meta">
                <div className="">
                  <label htmlFor="invoiceNbr" className="mb-1">
                    {t("invoice.invoiceNbr")}
                  </label>
                  {!invoice.isEditable && (
                    <strong className="d-block mb-2">
                      {invoice.invoiceNbr}
                    </strong>
                  )}
                  {invoice.isEditable && (
                    <input
                      type="text"
                      className={classNameForm}
                      name="invoiceNbr"
                      id="invoiceNbr"
                      value={edits.invoiceNbr || ""}
                      onChange={this.handleInputChange}
                    />
                  )}
                </div>
                <div className="">
                  <label htmlFor="invoiceDate" className="mb-1">
                    {t("invoice.invoiceDate")}
                  </label>
                  {!invoice.isEditable && (
                    <strong className="d-block mb-2">
                      {invoice.invoiceDate}
                    </strong>
                  )}
                  {invoice.isEditable && (
                    <Flatpickr
                      options={{
                        altFormat: "d/m/Y",
                        dateFormat: "Y-m-d",
                        altInput: true,
                      }}
                      value={edits.invoiceDate}
                      onChange={this.handleDateChange}
                      ref={(ref) => (this.invoiceDateRef = ref)}
                      name="invoiceDate"
                      id="invoiceDate"
                    />
                  )}
                </div>

                <div className="">
                  <label htmlFor="invoiceDate" className="mb-1">
                    {t("invoice.dueDate")}
                  </label>
                  {!invoice.isEditable && (
                    <strong className="d-block mb-2">{invoice.dueDate}</strong>
                  )}
                  {invoice.isEditable && (
                    <Flatpickr
                      options={{
                        altFormat: "d/m/Y",
                        dateFormat: "Y-m-d",
                        altInput: true,
                      }}
                      value={edits.dueDate}
                      onChange={this.handleDateChange}
                      ref={(ref) => (this.invoiceDueDateRef = ref)}
                      name="dueDate"
                      id="dueDate"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col">
              <label htmlFor="title">{t("invoice.title")}</label>
              <input
                className="form-control"
                type="text"
                name="title"
                id="title"
                value={edits.title || ""}
                onChange={this.handleInputChange}
              />
            </div>
          </div>
        </header>
        {this.renderModal()}
      </React.Fragment>
    )
  }
}

export default withTranslation()(InvoiceHeader)
